import React, { createContext, useContext, useState, useEffect } from 'react';

const CookieContext = createContext();

export const useCookies = () => {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error('useCookies must be used within a CookieProvider');
  }
  return context;
};

export const CookieProvider = ({ children }) => {
  const [cookieConsent, setCookieConsent] = useState(null);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always required
    functional: false,
    analytics: false,
    marketing: false
  });

  // Check for existing consent on load
  useEffect(() => {
    const savedConsent = localStorage.getItem('cookie-consent');
    const savedPreferences = localStorage.getItem('cookie-preferences');
    const consentDate = localStorage.getItem('cookie-consent-date');
    
    if (savedConsent && consentDate) {
      const consentAge = Date.now() - parseInt(consentDate);
      const oneYear = 365 * 24 * 60 * 60 * 1000; // 1 year in milliseconds
      
      if (consentAge < oneYear) {
        setCookieConsent(savedConsent);
        if (savedPreferences) {
          setCookiePreferences(JSON.parse(savedPreferences));
        }
      } else {
        // Consent expired, show banner again
        setShowCookieBanner(true);
      }
    } else {
      setShowCookieBanner(true);
    }
  }, []);

  // Verification Log: Cookie & GDPR Status
  useEffect(() => {
    console.log('Verification: Cookie & GDPR Context initialized', {
      consentStatus: cookieConsent || 'not_set',
      bannerVisible: showCookieBanner,
      preferences: cookiePreferences
    });
  }, [cookieConsent, showCookieBanner, cookiePreferences]);

  const acceptAllCookies = () => {
    const preferences = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    
    setCookieConsent('accepted');
    setCookiePreferences(preferences);
    setShowCookieBanner(false);
    
    localStorage.setItem('cookie-consent', 'accepted');
    localStorage.setItem('cookie-preferences', JSON.stringify(preferences));
    localStorage.setItem('cookie-consent-date', Date.now().toString());
    
    // Initialize analytics if accepted
    if (preferences.analytics) {
      initializeAnalytics();
    }
  };

  const acceptNecessaryOnly = () => {
    const preferences = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    
    setCookieConsent('necessary-only');
    setCookiePreferences(preferences);
    setShowCookieBanner(false);
    
    localStorage.setItem('cookie-consent', 'necessary-only');
    localStorage.setItem('cookie-preferences', JSON.stringify(preferences));
    localStorage.setItem('cookie-consent-date', Date.now().toString());
  };

  const updatePreferences = (newPreferences) => {
    setCookiePreferences(newPreferences);
    setCookieConsent('custom');
    setShowCookieBanner(false);
    
    localStorage.setItem('cookie-consent', 'custom');
    localStorage.setItem('cookie-preferences', JSON.stringify(newPreferences));
    localStorage.setItem('cookie-consent-date', Date.now().toString());
    
    // Initialize or remove analytics based on preference
    if (newPreferences.analytics) {
      initializeAnalytics();
    } else {
      removeAnalytics();
    }
  };

  const resetConsent = () => {
    localStorage.removeItem('cookie-consent');
    localStorage.removeItem('cookie-preferences');
    localStorage.removeItem('cookie-consent-date');
    setCookieConsent(null);
    setCookiePreferences({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    });
    setShowCookieBanner(true);
  };

  const initializeAnalytics = () => {
    // Initialize Google Analytics or other analytics here
    console.log('Analytics initialized based on user consent');
  };

  const removeAnalytics = () => {
    // Remove analytics cookies and scripts
    console.log('Analytics removed based on user preference');
  };

  const value = {
    cookieConsent,
    cookiePreferences,
    showCookieBanner,
    acceptAllCookies,
    acceptNecessaryOnly,
    updatePreferences,
    resetConsent,
    setShowCookieBanner
  };

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  );
};